import React from 'react';
import { Box, Typography, Avatar, Paper, Container } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupIcon from '@mui/icons-material/Group';
import MapIcon from '@mui/icons-material/Map';
import { AppButton } from 'components/widgets/AppButton';
import { AppSectionView } from 'components/utils/AppSectionView';
import { AppStatView } from 'components/utils/AppStatsView';
import { AppHeroView } from 'components/utils/AppHeroView';
import { useApp } from 'AppProvider';
import ContactUsPage from './ContactUsPage';
import avis_owner_photo from 'assets/images/avis_owner_photo.jpg'; // Replace with actual photo

const HomePage = () => {
    const { navigateToPage } = useApp();

    const openContactUsPage = () => {
        navigateToPage(<ContactUsPage />);
    };

    const stats = [
        { value: '700+', label: 'Vehicles per Month' },
        { value: '300+', label: 'Expert Drivers' },
        { value: 'Pan India', label: 'Coverage' },
        { value: '99%', label: 'On-Time Delivery' },
    ];

    const features = [
        {
            icon: <LocalShippingIcon fontSize="large" />,
            title: 'Commercial Vehicle Transport',
            description: 'Specialized in Chassis and FBV movement across India',
        },
        {
            icon: <TimelineIcon fontSize="large" />,
            title: 'Convoy Management',
            description: 'Efficient convoy mode transportation for bulk movements',
        },
        {
            icon: <GroupIcon fontSize="large" />,
            title: 'Expert Team',
            description: 'Dedicated team of 200+ professional drivers',
        },
        {
            icon: <MapIcon fontSize="large" />,
            title: 'Pan India Service',
            description: 'Comprehensive coverage across all major routes',
        },
    ];

    // Owner Info Section
    const owner = {
        name: "BIPIN KUMAR SINGH",
        photo: avis_owner_photo,//"https://randomuser.me/api/portraits/men/32.jpg", // Replace with actual photo
        bio: "Founder & CEO with 8+ years of hands-on experience in logistics and commercial vehicle transportation. Driven by a vision to set new standards in reliability, transparency, and customer satisfaction, Bipin leads Avis Logistics with a focus on operational excellence and innovation. His expertise spans convoy management, route optimization, and building high-performance teams. Committed to forging long-term partnerships, he ensures every client receives tailored, end-to-end solutions that empower their business growth. Bipin’s agenda is to make vehicle movement seamless, secure, and efficient across India, while upholding the highest standards of integrity and professionalism."
    };

    // Additional phrases for motive and agenda
    const motiveText = 
        "At Avis Logistics, our core motive is to revolutionize the commercial vehicle transportation industry by setting new benchmarks in reliability, transparency, and customer-centric service. We believe that every vehicle entrusted to us is not just cargo, but a commitment to our client’s business and reputation. " +
        "Our agenda is to provide seamless, end-to-end logistics solutions that empower manufacturers, dealers, and body builders to expand their reach across India without worrying about the complexities of vehicle movement. Through our robust convoy management, experienced drivers, and advanced tracking systems, we ensure every delivery is executed with precision and accountability. " +
        "We are dedicated to building long-term partnerships by consistently exceeding expectations, investing in technology, and nurturing a team of professionals who share our passion for excellence. Our vision is to be recognized as the most dependable and innovative logistics partner in the industry, where trust and safety are never compromised. " +
        "Your vehicles, our responsibility – from the first mile to the last, we guarantee timely, secure, and hassle-free transportation, so you can focus on growing your business while we take care of the journey. " +
        "We continuously strive to innovate and adapt to the evolving needs of our clients, ensuring that our solutions remain ahead of the curve. Our commitment to sustainability and ethical practices drives us to operate responsibly, respecting both our clients and the environment. " +
        "At Avis Logistics, we don’t just move vehicles—we move businesses forward, one journey at a time.";

    return (
        <Box>
            {/* Hero Section */}
            <AppHeroView
                title="Your Trusted Partner for Commercial Vehicle Transport"
                description="Specializing in the movement of Commercial Vehicles, including Chassis and FBVs, across India. We collaborate with Body Builders, Dealers, and Direct Customers to ensure seamless Pan India transportation. With a robust convoy system and a fleet of 300+ expert drivers, we handle 700+ vehicles monthly, delivering on time with unmatched reliability."
                renderBottomContent={() => (
                    <AppButton color="secondary" size="large" sx={{ mt: 2 }} onClick={openContactUsPage}>
                        Get Started
                    </AppButton>
                )}
            />

            {/* /* Motive & Agenda Section */}
            <Container maxWidth="lg">
                <Box sx={{ my: 6, px: 2, textAlign: 'center' }}>
                    <Typography
                        variant="h4"
                        gutterBottom
                        color="primary"
                        sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
                    >
                        Our Motive & Agenda
                    </Typography>
                    {/* {motivePhrases.map((phrase, idx) => ( */}
                    <Typography variant="subtitle1" sx={{
                                fontSize: 18,
                                mx: 'auto',
                                textAlign: 'justify',
                                animation: 'fadeIn 1.2s 0.4s both',
                                color: "text.secondary" 
                            }}>
                        {motiveText}
                    </Typography>
                {/* ))} */}
            </Box>
            </Container>

            {/* Stats Section */}
            <AppSectionView
                itemContainerProps={{ itemProps: { textAlign: 'center' } }}
                items={stats}
                renderItem={(item, index) => <AppStatView key={index} {...item} />}
            />

            {/* Features Section */}
            <AppSectionView title="Our Services" items={features} />

            {/* Owner Information Section */}
            <Box sx={{ my: 8, px: 2 }}>
                <Paper
                    elevation={4}
                    sx={{
                        p: 5,
                        maxWidth: "lg",
                        mx: 'auto',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #fff 100%)',
                        boxShadow: 6,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            animation: 'fadeInUp 1s ease',
                            '@keyframes fadeInUp': {
                                from: { opacity: 0, transform: 'translateY(40px)' },
                                to: { opacity: 1, transform: 'translateY(0)' },
                            },
                        }}
                    >
                        <Avatar
                            src={owner.photo}
                            alt={owner.name}
                            sx={{
                                width: 160,
                                height: 160,
                                mx: 'auto',
                                mb: 3,
                                boxShadow: 4,
                                border: '5px solid #1976d2',
                                transition: 'transform 0.4s cubic-bezier(.4,2,.6,1)',
                                '&:hover': {
                                    transform: 'scale(1.07) rotate(-2deg)',
                                    boxShadow: 8,
                                },
                            }}
                        />
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={{
                                fontWeight: 'bold',
                                color: 'primary.main',
                                letterSpacing: 1,
                                mb: 1,
                                animation: 'fadeIn 1.2s 0.2s both',
                                '@keyframes fadeIn': {
                                    from: { opacity: 0 },
                                    to: { opacity: 1 },
                                },
                            }}
                        >
                            {owner.name}
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{
                                textAlign: 'justify',
                                fontSize: 20,
                                mx: 'auto',
                                animation: 'fadeIn 1.2s 0.4s both',
                            }}
                        >
                            {owner.bio}
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            {/* CTA Section */}
            <AppHeroView
                sx={{ textAlign: "center", py: 8 }}
                title="Ready to Move Your Vehicles?"
                description="Contact us for efficient and reliable transport solutions"
                titleProps={{ variant: 'h4', gutterBottom: true }}
                descriptionProps={{ variant: 'subtitle1', paragraph: true }}
                renderBottomContent={() => (
                    <AppButton variant="contained" color="primary" size="large" onClick={openContactUsPage}>
                        Contact Us
                    </AppButton>
                )}
            />
        </Box>
    );
};

export default HomePage;